const API_BASE_URL = process.env.REACT_APP_API_ADDRESS;

// Upload a new file for a user
export const uploadUserFile = async (profileId, processType = 'other', file) => {
  try {
    const url = new URL(`${API_BASE_URL}/accounts/user-files/`);
    const formData = new FormData();
    formData.append("profile", profileId);
    formData.append("process_type", processType);
    formData.append("file", file);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: formData,
    });

    const data = await response.json();
    return {
      success: response.ok,
      data: response.ok ? data : null,
      errors: !response.ok ? data : null,
      statusCode: response.status,
    };
  } catch (error) {
    console.error("Upload user file error", error);
    return {
      success: false,
      data: null,
      statusCode: 500,
      error: error.message,
    };
  }
};

// Fetch all files (paginated)
export const fetchAllFiles = async (page = 1, pageSize = 10) => {
  try {
    const url = new URL(`${API_BASE_URL}/accounts/user-files/`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('page_size', String(pageSize));

    const response = await fetch(url, {
      method: "GET",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });

    const raw = await response.json();

    if (!response.ok) {
      return {
        success: false,
        data: null,
        errors: raw,
        statusCode: response.status,
      };
    }

    // DRF pagination format: { count, next, previous, results }
    const items = Array.isArray(raw)
      ? raw
      : Array.isArray(raw.results)
        ? raw.results
        : [];

    const data = {
      items,
      count: typeof raw.count === 'number' ? raw.count : items.length,
      next: raw.next || null,
      previous: raw.previous || null,
    };

    return {
      success: true,
      data,
      errors: null,
      statusCode: response.status,
    };
  } catch (error) {
    console.error("Fetch all files error", error);
    return {
      success: false,
      data: null,
      statusCode: 500,
      error: error.message,
    };
  }
};


// Fetch files for a specific user (paginated)
export const fetchFilesByUser = async (
  userId,
  page = 1,
  pageSize = 10,
  processType = null
) => {
  try {
    const url = new URL(`${API_BASE_URL}/accounts/user-files/files_by_user/`);
    url.searchParams.set('user_id', String(userId));
    url.searchParams.set('page', String(page));
    url.searchParams.set('page_size', String(pageSize));

    if (processType) {
      url.searchParams.set('process_type', processType);
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });

    const raw = await response.json();

    if (!response.ok) {
      return {
        success: false,
        data: null,
        errors: raw,
        statusCode: response.status,
      };
    }

    const items = Array.isArray(raw)
      ? raw
      : Array.isArray(raw.results)
        ? raw.results
        : [];

    const data = {
      items,
      count: typeof raw.count === 'number' ? raw.count : items.length,
      next: raw.next || null,
      previous: raw.previous || null,
    };

    return {
      success: true,
      data,
      errors: null,
      statusCode: response.status,
    };
  } catch (error) {
    console.error("Fetch files by user error", error);
    return {
      success: false,
      data: null,
      statusCode: 500,
      error: error.message,
    };
  }
};

// Search files for a specific user by query text (paginated)
export const searchFilesByUser = async (
  userId,
  query,
  page = 1,
  pageSize = 10,
  processType = null
) => {
  try {
    const url = new URL(`${API_BASE_URL}/accounts/user-files/search_by_text/`);
    url.searchParams.set('user_id', String(userId));
    url.searchParams.set('q', query || '');
    url.searchParams.set('page', String(page));
    url.searchParams.set('page_size', String(pageSize));

    if (processType) {
      url.searchParams.set('process_type', processType);
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    });

    const raw = await response.json();

    if (!response.ok) {
      return {
        success: false,
        data: null,
        errors: raw,
        statusCode: response.status,
      };
    }

    const items = Array.isArray(raw)
      ? raw
      : Array.isArray(raw.results)
        ? raw.results
        : [];

    const data = {
      items,
      count: typeof raw.count === 'number' ? raw.count : items.length,
      next: raw.next || null,
      previous: raw.previous || null,
    };

    return {
      success: true,
      data,
      errors: null,
      statusCode: response.status,
    };
  } catch (error) {
    console.error('Search files by user error', error);
    return {
      success: false,
      data: null,
      statusCode: 500,
      error: error.message,
    };
  }
};


// Download a file by ID
const triggerDownloadFromBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'subor';
  a.click();
  window.URL.revokeObjectURL(url);
};

export const downloadFile = async (file) => {
  try {
    const fileId = file.id; // we assume full file object is passed

    const url = new URL(
      `${API_BASE_URL}/accounts/user-files/${fileId}/download_file/`
    );

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || 'Error downloading file');
    }

    const blob = await response.blob();

    // we already KNOW the name on frontend, no header parsing
    const filename =
      file.downloadName ||
      file.filename ||
      `anonymizovany_${file.name || 'subor'}`;

    triggerDownloadFromBlob(blob, filename);

    return { success: true };
  } catch (error) {
    console.error('Download file error', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

const fetchFileBlob = async (file) => {
  const fileId = file.id;

  const url = new URL(
    `${API_BASE_URL}/accounts/user-files/${fileId}/download_file/`
  );

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    },
  });

  if (!response.ok) {
    let msg = 'Error downloading file';
    try {
      const data = await response.json();
      msg = data.detail || msg;
    } catch { }
    throw new Error(msg);
  }

  return await response.blob();
};

export const downloadFileForPreview = async (file) => {
  try {
    const blob = await fetchFileBlob(file);
    return { success: true, data: { blob } };
  } catch (error) {
    console.error('Download preview error', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Delete a file by ID
export const deleteFile = async (fileId) => {
  try {
    const url = new URL(`${API_BASE_URL}/accounts/user-files/${fileId}/`);
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || "Error deleting file");
    }

    return { success: true };
  } catch (error) {
    console.error("Delete file error", error);
    return {
      success: false,
      error: error.message,
    };
  }
};
